import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Button, IconButton, Dialog, DialogContent, DialogTitle, CircularProgress, Menu, MenuItem } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { transactionsService, OrderPayment, TransactionSummary } from '../../services/transactions.service';

interface OrderPaymentsProps {
  activeTab: 'payout-requests' | 'order-payments';
  setActiveTab: (tab: 'payout-requests' | 'order-payments') => void;
  tabs: Array<{ key: string; label: string; badgeCount: number }>;
  searchQuery: string;
}

const OrderPayments: React.FC<OrderPaymentsProps> = ({ activeTab, setActiveTab, tabs, searchQuery }) => {
  const [payments, setPayments] = useState<OrderPayment[]>([]);
  const [summary, setSummary] = useState<TransactionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPayment, setSelectedPayment] = useState<OrderPayment | null>(null);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(null);
  const ITEMS_PER_PAGE = 10;

  const fetchOrderPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      // Use getAllCheckouts endpoint with status filter
      const paymentStatusParam = statusFilter !== 'all' ? statusFilter : undefined;
      const response = await transactionsService.getAllCheckouts(currentPage, 10, undefined, paymentStatusParam);
      
      if (!response || !response.success || !response.data) {
        console.error('Invalid response structure:', response);
        setError('Invalid response from server');
        setLoading(false);
        return;
      }
      
      const serviceResponse = response.data;
      
      if (!serviceResponse.data || !Array.isArray(serviceResponse.data)) {
        console.error('Service data is not an array:', serviceResponse);
        setError('Invalid data format from server');
        setLoading(false);
        return;
      }
      
      // Transform checkouts to order payments format
      const orderPayments: OrderPayment[] = serviceResponse.data.map((checkout: any) => {
        return {
        id: checkout.checkoutId,
        orderId: checkout.orderIds && checkout.orderIds.length > 0 ? checkout.orderIds[0] : checkout.checkoutId, // Use first orderId if available, fallback to checkoutId
        date: new Date(checkout.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
        customer: checkout.customerName || 'Unknown Customer',
        phone: checkout.customerPhone || '+974 XXX XXX XXXX',
        paymentMethod: 'mastercard' as const,
        cardLast4: checkout.latestVisaId ? checkout.latestVisaId.slice(-4) : undefined,
        paymentId: checkout.latestPaymentId,
        amount: `QAR ${checkout.totalAmount.toFixed(2)}`,
        eventId: checkout.eventId,
        eventName: checkout.eventName,
        vendorId: checkout.paymentStatus, // Store payment status here for filtering
        vendorName: checkout.customerName,
        feePercentage: checkout.feePercentage,
        feeAmount: checkout.feeAmount,
        vendorPayout: checkout.vendorPayout,
      };
      });
      
      setPayments(orderPayments);
      setSummary(serviceResponse.summary);
      setTotalPages(serviceResponse.pagination.totalPages);
      setTotalItems(serviceResponse.pagination.totalItems);
    } catch (error) {
      console.error('Error fetching order payments:', error);
      setError('Failed to load order payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderPayments();
  }, [currentPage, statusFilter]);

  // Client-side search filtering (only filters current page results)
  // For better UX, search applies to the data already filtered by backend status filter
  const filteredPayments = useMemo(() => {
    if (!searchQuery.trim()) {
      return payments;
    }
    
    const query = searchQuery.toLowerCase();
    return payments.filter(payment => 
      payment.date.toLowerCase().includes(query) ||
      payment.orderId.toLowerCase().includes(query) ||
      payment.customer.toLowerCase().includes(query) ||
      payment.phone.toLowerCase().includes(query) ||
      (payment.paymentId && payment.paymentId.toLowerCase().includes(query)) ||
      (payment.cardLast4 && payment.cardLast4.includes(query)) ||
      payment.amount.toLowerCase().includes(query)
    );
  }, [payments, searchQuery]);

  // Calculate display counts for pagination
  // When searching, show filtered count; otherwise show backend total
  const displayTotalItems = searchQuery.trim() ? filteredPayments.length : totalItems;
  const displayTotalPages = searchQuery.trim() ? 1 : totalPages; // Search shows all results on one page

  const handleOrderClick = async (orderId: string, payment?: OrderPayment) => {
    setSelectedOrderId(orderId);
    setSelectedPayment(payment || null);
    setIsModalOpen(true);
    setLoadingOrderDetails(true);
    
    try {
      const response = await transactionsService.getOrderDetails(orderId);
      console.log('Order details response:', response);
      // Handle nested response structure: response.data can be the order directly or response.data.data
      const orderData = response.data?.data || response.data;
      setOrderDetails(orderData);
    } catch (error) {
      console.error('Error fetching order details:', error);
      setOrderDetails(null);
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrderId(null);
    setOrderDetails(null);
  };

  // More menu handlers
  const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>, payment: OrderPayment) => {
    setMoreMenuAnchorEl(event.currentTarget);
    setSelectedPayment(payment);
  };

  const handleMoreMenuClose = () => {
    setMoreMenuAnchorEl(null);
    setSelectedPayment(null);
  };

  const handleViewDetails = () => {
    if (selectedPayment) {
      handleOrderClick(selectedPayment.orderId, selectedPayment);
    }
    handleMoreMenuClose();
  };

  // Status filter handlers
  const handleStatusFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setStatusAnchorEl(event.currentTarget);
  };

  const handleStatusFilterClose = () => {
    setStatusAnchorEl(null);
  };

  const handleStatusFilterSelect = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1); // Reset to first page when filter changes
    // Note: searchQuery is controlled by parent component
    handleStatusFilterClose();
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      all: 'Any status',
      new: 'New',
      pending: 'Pending',
      completed: 'Completed',
      failed: 'Failed',
    };
    return labels[status] || 'Any status';
  };

  // Pagination helper function
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const pagesToShow = displayTotalPages;
    
    if (pagesToShow <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= pagesToShow; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage <= 3) {
        // Near start: show 1, 2, 3, 4, ..., last
        pages.push(2, 3, 4, '...', pagesToShow);
      } else if (currentPage >= pagesToShow - 2) {
        // Near end: show 1, ..., last-3, last-2, last-1, last
        pages.push('...', pagesToShow - 3, pagesToShow - 2, pagesToShow - 1, pagesToShow);
      } else {
        // Middle: show 1, ..., current-1, current, current+1, ..., last
        pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', pagesToShow);
      }
    }
    
    return pages;
  };

  const renderPaymentMethod = (payment: OrderPayment) => {
    if (payment.paymentMethod === 'apple-pay') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Box sx={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '16px' }}>🍎</Typography>
          </Box>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
            Apple Pay
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Box sx={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: '16px' }}>💳</Typography>
        </Box>
        <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
          • {payment.cardLast4 || 'XXXX'}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      {/* More Actions Menu */}
      <Menu
        anchorEl={moreMenuAnchorEl}
        open={Boolean(moreMenuAnchorEl)}
        onClose={handleMoreMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>View Details</MenuItem>
      </Menu>

      {/* Status Filter Menu */}
      <Menu
        anchorEl={statusAnchorEl}
        open={Boolean(statusAnchorEl)}
        onClose={handleStatusFilterClose}
      >
        <MenuItem onClick={() => handleStatusFilterSelect('all')}>Any status</MenuItem>
        <MenuItem onClick={() => handleStatusFilterSelect('new')}>New</MenuItem>
        <MenuItem onClick={() => handleStatusFilterSelect('pending')}>Pending</MenuItem>
        <MenuItem onClick={() => handleStatusFilterSelect('completed')}>Completed</MenuItem>
        <MenuItem onClick={() => handleStatusFilterSelect('failed')}>Failed</MenuItem>
      </Menu>

      {/* Order Details Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 'var(--palette-light-4-value)',
            maxWidth: '800px',
            backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--palette-light-6-value)', backgroundColor: 'var(--palette-light-background-secondary-value-hex)' }}>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-h2-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
            Order #{selectedOrderId}
          </Typography>
          <IconButton onClick={handleCloseModal} sx={{ color: 'var(--palette-light-text-secondary-value-hex)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: 'var(--palette-light-6-value)', display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-6-value)', backgroundColor: 'var(--palette-light-background-secondary-value-hex)' }}>
          {loadingOrderDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <CircularProgress />
            </Box>
          ) : !orderDetails ? (
            <Typography color="error">Failed to load order details</Typography>
          ) : (
            <>
              {/* Order Info Section */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)' }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
                  Order Information
                </Typography>
                
                {/* Order Details Grid */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--palette-light-4-value)' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                      Order ID
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                      #{orderDetails.orderId}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                      Date & Time
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                      {orderDetails.createdAt ? new Date(orderDetails.createdAt).toLocaleString() : 'N/A'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                      Customer
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                      {orderDetails.contactDetails?.contactName || orderDetails.customer?.name || 'N/A'}
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                      {orderDetails.contactDetails?.phoneNumber || orderDetails.customer?.phone || 'N/A'}
                    </Typography>
                    {orderDetails.contactDetails?.email && (
                      <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                        {orderDetails.contactDetails.email}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                      Event
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                      {orderDetails.eventDetails?.eventName || 'N/A'}
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                      {orderDetails.eventDetails?.eventDate || 'N/A'}
                    </Typography>
                    {orderDetails.eventDetails?.eventAddress && (
                      <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                        {orderDetails.eventDetails.eventAddress}, {orderDetails.eventDetails.eventZone}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Product/Service Section */}
              {orderDetails.productDetails && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)' }}>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
                    Service
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-2-value)', padding: 'var(--palette-light-4-value)', backgroundColor: 'var(--palette-light-background-primary-value-hex)', borderRadius: 'var(--palette-light-3-value)' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-1-value)', flex: 1 }}>
                        <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
                          {orderDetails.productDetails.title || 'Service'}
                        </Typography>
                        {orderDetails.startDateTime && (
                          <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                            {new Date(orderDetails.startDateTime).toLocaleString()}
                          </Typography>
                        )}
                        {orderDetails.specialRequests && (
                          <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                            Special requests: {orderDetails.specialRequests}
                          </Typography>
                        )}
                      </Box>
                      <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
                        QAR {orderDetails.amount?.toFixed(2) || '0.00'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Payment Section */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)', paddingTop: 'var(--palette-light-4-value)', borderTop: '1px solid var(--palette-light-border-light-value-hex)' }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
                  Payment Details
                </Typography>
                
                {/* Payment ID and Card Info */}
                {selectedPayment && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--palette-light-4-value)', marginBottom: 'var(--palette-light-4-value)' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                        Payment ID
                      </Typography>
                      <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontFamily: 'monospace', color: 'var(--palette-light-text-primary-value-hex)' }}>
                        {selectedPayment.paymentId || 'N/A'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                        Card
                      </Typography>
                      <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontFamily: 'monospace', color: 'var(--palette-light-text-primary-value-hex)' }}>
                        {selectedPayment.cardLast4 ? `****${selectedPayment.cardLast4}` : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                    Subtotal
                  </Typography>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    QAR {orderDetails.amount?.toFixed(2) || '0.00'}
                  </Typography>
                </Box>
                
                {orderDetails.discountAmount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                      Discount ({orderDetails.discountType})
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-status-success-value-hex)' }}>
                      -QAR {orderDetails.discountAmount?.toFixed(2)}
                    </Typography>
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
                    Total
                  </Typography>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-h3-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
                    QAR {((orderDetails.amount || 0) - (orderDetails.discountAmount || 0)).toFixed(2)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-1-value)' }}>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                    Status
                  </Typography>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    {orderDetails.status || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px', flex: 1, overflow: 'hidden' }}>
        {/* Stats Card */}
        {summary && (
          <Box
            sx={{
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
              borderRadius: '16px',
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'stretch', alignItems: 'stretch', gap: 'var(--palette-light-4-value)', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '200px' }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
                  Stats for
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700, color: 'var(--palette-light-brand-primary-value-hex)' }}>
                    this month
                  </Typography>
                  <KeyboardArrowDownIcon sx={{ fontSize: '24px', color: 'var(--palette-light-brand-primary-value-hex)' }} />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '200px' }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
                  QAR {parseFloat(summary.totalAmount || '0').toLocaleString()}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    Total orders
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '200px' }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
                  QAR {parseFloat(summary.vendorPayouts || summary.totalAmount || '0').toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                  Payouts
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '200px' }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
                  QAR {parseFloat(summary.eventiFee || '0').toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                  Eventi fee ({summary.averageFeePercentage || '0'}% avg)
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {loading && payments.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <>
            {/* Tabs and Filters */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              {/* Tabs */}
              <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {tabs.map((tab) => (
                  <Box
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key as typeof activeTab)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px 7px',
                      height: '39px',
                      backgroundColor:
                        tab.key === activeTab
                          ? 'var(--palette-light-brand-secondary-value-hex)'
                          : 'var(--palette-light-background-secondary-value-hex)',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.9,
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 'var(--palette-light-font-size-default-value)',
                        color:
                          tab.key === activeTab
                            ? 'var(--palette-light-text-foreground-value-hex)'
                            : 'var(--palette-light-text-primary-value-hex)',
                      }}
                    >
                      {tab.label}
                    </Typography>
                    {tab.badgeCount > 0 && (
                      <Box
                        sx={{
                          backgroundColor: tab.key === activeTab ? 'var(--palette-light-text-foreground-value-hex)' : 'var(--palette-light-status-success-value-hex)',
                          color: tab.key === activeTab ? 'var(--palette-light-text-primary-value-hex)' : 'var(--palette-light-text-foreground-value-hex)',
                          fontSize: 'var(--palette-light-font-size-small-value)',
                          fontWeight: 700,
                          height: '20px',
                          minWidth: '20px',
                          borderRadius: '20px',
                          border: tab.key === activeTab ? 'none' : '2px solid var(--palette-light-background-primary-value-hex)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '2px 6px 0',
                        }}
                      >
                        {tab.badgeCount}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
              
              {/* Filters */}
              <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {/* Status Filter */}
                <Button
                  onClick={handleStatusFilterClick}
                  sx={{
                    backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                    color: 'var(--palette-light-text-primary-value-hex)',
                    fontSize: 'var(--palette-light-font-size-default-value)',
                    height: '39px',
                    padding: '10px 20px 7px',
                    borderRadius: '16px',
                    textTransform: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    '&:hover': {
                      backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                      opacity: 0.9,
                    },
                  }}
                >
                  {getStatusLabel(statusFilter)}
                  <KeyboardArrowDownIcon sx={{ fontSize: '20px' }} />
                </Button>
              </Box>
            </Box>

            {/* Table */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Box sx={{ backgroundColor: 'var(--palette-light-background-primary-value-hex)', width: '100%', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Table Header */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: '16px',
                    padding: '12px 0px',
                    borderBottom: '1px solid var(--palette-light-border-primary-value-hex)',
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ width: '120px', display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                      Date/time
                    </Typography>
                  </Box>
                  <Box sx={{ width: '180px', display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                      Order
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                      Customer
                    </Typography>
                  </Box>
                  <Box sx={{ width: '160px', display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                      Payment ID
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100px', display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                      Card
                    </Typography>
                  </Box>
                  <Box sx={{ width: '80px', display: 'flex', justifyContent: 'flex-end', gap: '10px', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                      Fee %
                    </Typography>
                  </Box>
                  <Box sx={{ width: '120px', display: 'flex', justifyContent: 'flex-end', gap: '10px', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                      Amount
                    </Typography>
                  </Box>
                  <Box sx={{ width: '56px', flexShrink: 0 }} />
                </Box>

                {/* Table Rows Container with Scroll */}
                <Box sx={{ 
                  flex: 1, 
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'var(--palette-light-text-tertiary-value-hex)',
                    borderRadius: '4px',
                  },
                }}>
                  {/* Table Rows */}
                  {filteredPayments.map((payment) => (
                    <Box
                      key={payment.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        padding: '8px 0px',
                        borderBottom: '1px solid var(--palette-light-border-primary-value-hex)',
                        minWidth: '1000px',
                      }}
                    >
                      <Box sx={{ width: '120px', display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
                        <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                          {payment.date}
                        </Typography>
                      </Box>
                      <Box sx={{ width: '180px', display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
                        <Typography
                          onClick={() => handleOrderClick(payment.orderId, payment)}
                          sx={{
                            fontSize: 'var(--palette-light-font-size-default-value)',
                            fontWeight: 700,
                            color: 'var(--palette-light-brand-primary-value-hex)',
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {payment.orderId}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                          {payment.customer}
                        </Typography>
                        <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                          {payment.phone}
                        </Typography>
                      </Box>
                      <Box sx={{ width: '160px', display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
                        <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontFamily: 'monospace', color: 'var(--palette-light-text-primary-value-hex)' }}>
                          {payment.paymentId || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ width: '100px', display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
                        <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                          {payment.cardLast4 ? `****${payment.cardLast4}` : 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ width: '80px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                          {payment.feePercentage ? `${payment.feePercentage.toFixed(1)}%` : 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ width: '120px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                          {payment.amount}
                        </Typography>
                      </Box>
                      <Box sx={{ width: '56px', height: '56px', display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
                        <IconButton
                          onClick={(e) => handleMoreMenuOpen(e, payment)}
                          sx={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '16px',
                          }}
                        >
                          <MoreHorizIcon sx={{ color: 'var(--palette-light-brand-primary-value-hex)' }} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Pagination */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '24px',
                  mt: '24px',
                  flexShrink: 0,
                  flexWrap: 'wrap',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    padding: '4px',
                  }}
                >
                  {!searchQuery.trim() && getPageNumbers().map((page, index) => {
                    if (page === '...') {
                      return (
                        <Typography
                          key={`ellipsis-${index}`}
                          sx={{
                            fontSize: 'var(--palette-light-font-size-default-value)',
                            color: 'var(--palette-light-text-primary-value-hex)',
                            padding: '0 8px',
                          }}
                        >
                          ...
                        </Typography>
                      );
                    }
                    
                    const pageNum = page as number;
                    return (
                      <Button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        sx={{
                          backgroundColor: pageNum === currentPage ? '#F4DBC5' : 'transparent',
                          borderRadius: pageNum === currentPage ? '50%' : '6px',
                          minWidth: '56px',
                          width: '56px',
                          height: '56px',
                          fontSize: 'var(--palette-light-font-size-default-value)',
                          fontWeight: pageNum === currentPage ? 700 : 400,
                          color: pageNum === currentPage ? 'var(--palette-light-text-foreground-value-hex)' : 'var(--palette-light-text-primary-value-hex)',
                          textTransform: 'none',
                          padding: 0,
                          '&:hover': {
                            backgroundColor: pageNum === currentPage ? '#F4DBC5' : 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  {!searchQuery.trim() && currentPage < displayTotalPages && (
                    <Button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      sx={{
                        backgroundColor: 'transparent',
                        borderRadius: '6px',
                        minWidth: 'auto',
                        padding: '0 32px',
                        height: '56px',
                        fontSize: 'var(--palette-light-font-size-default-value)',
                        fontWeight: 400,
                        color: 'var(--palette-light-text-primary-value-hex)',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      Next
                    </Button>
                  )}
                </Box>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                  {searchQuery.trim() 
                    ? `Showing ${displayTotalItems} search result${displayTotalItems !== 1 ? 's' : ''}`
                    : `Page ${currentPage} of ${displayTotalPages} (${displayTotalItems} total orders)`
                  }
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

export default OrderPayments;
